import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FilterItems, FilterLabels, SelectedItemList, DateRangeFilter, DateRangeOption, DateRangeTypes, ReportType, DynamicReportView, DateRangeReportOption } from '../interfaces/filter-data';
import { ReportAction, Reports } from '../interfaces/reports';
import { clone } from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { BreadcrumbService, IBreadcrumb } from '../../shared/breadcrumb-service/breadcrumb.service';
import { UrlBuilder } from '../../../core/url-builder/url-builder';
import { UserFilter } from '../../../covance-filter/filters-storage';
import { SavedFilter } from '../interfaces/saved-filters';
import { ReportsStorageService } from '../services/reports-storage-service';
import { ActivityLogger } from '../../../activities/activity-logger';
import { combineLatest } from 'rxjs';
import * as moment from 'moment';
import { ReportColumnsService } from '../services/report-columns-service';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';
import { DynamicListViewColumnData } from '../../filter-samples/interface/dynamic-list-info';
import { ColumnSettingInfo } from '../../../shared/covance-grid/column-settings/column-settings';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-new-report',
    template: require('./new-report.component.html'),
    styles: [require('./new-report.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class NewReportComponent {

    public filterInfo: FilterLabels = {
        title: 'Report Name',
        newText: 'New Report',
        editText: 'Edit Report',
        deleteText: 'Delete Report',
        infoMsg: 'Select from your Saved Reports Definitions or type to add new report',
        menuToolTip: 'Create, edit or delete reports',
        groupName: 'generate-report',

    };
    public filterErrorMsg: string;
    public reportMsg: string;
    public showMyFilterLoading = false;
    public showActionsLoading = false;
    public selectedItemList: SelectedItemList = {} as SelectedItemList;
    public myFilterItems: UserFilter[];
    public selectedFilter: SavedFilter;
    public showSaveOptions = false;
    public showScheduleOptions = false;
    public disableSchedule = false;
    public disableSave = false;
    public disableGenerate = false;
    public isScheduleRoute = false;
    public title: string;
    public preSelectedFilterItem: UserFilter;
    public isReportNameError = false;
    public clearSelectedFilters = false;
    public showDataLoadingError = false;
    public dateRangeOptions: DateRangeFilter[] = [];
    public selectedDateRange: DateRangeOption;
    public selectedReportDateRange: DateRangeReportOption;
    public columnSettingsInfo: ColumnSettingInfo;
    public dynamicColumnData: DynamicListViewColumnData;
    public expandEditView = false;
    public isGeneratedNotSavedReport = false;

    constructor(
        private reportService: ReportsStorageService,
        private activatedRoute: ActivatedRoute,
        private breadCrumbService: BreadcrumbService,
        private urlBuilder: UrlBuilder,
        private changeDectRef: ChangeDetectorRef,
        private reportColumnService: ReportColumnsService,
        private activityLogger: ActivityLogger,

    ) {
        combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.queryParams
        ]).subscribe(param => {
            const params = { ...param[0], ...param[1] };

            this.selectedItemList.reportType = +params.reportTypeId;
            this.selectedItemList.isGeneratedNotSavedReport = this.isGeneratedNotSavedReport = params.isGeneratedNotSavedReport;
            this.dateRangeOptions = this.reportColumnService.getDateRangeOptions(+params.reportTypeId);
            this.showScheduleOptions = this.isScheduleRoute = (params.reportOption === 'schedule');

            this.title = this.isScheduleRoute ? 'Create a Scheduled Report' : 'Generate a New Report';

            if (this.showScheduleOptions) {
                this.disableAllActionButtons();
            }
            this.getDynamicReportColumns(+params.reportTypeId);
            this.getSavedReports(+params.reportId || 0);
            let routerUrl: IBreadcrumb = {
                label: this.title,
                url: this.isScheduleRoute
                    ? this.urlBuilder.esa().sampletracking().report().NewScheduledReport(this.selectedItemList.reportType).url()
                    : this.urlBuilder.esa().sampletracking().report().NewGenerateReport(this.selectedItemList.reportType).url()
            };
            this.breadCrumbService.AddRouterToBreadCrumb(routerUrl);
        });
    }

    public async onEditViewClick() {
        if (!this.columnSettingsInfo) { return; }
        const result = await this.reportColumnService.openEditDialog(this.columnSettingsInfo).toPromise();
        if (result) {
            if (result.resetSettings) {
                let resetColumnSettings = await this.reportColumnService.showResetDialogConfirmation(this.dynamicColumnData, result, this.selectedFilter);
                this.columnSettingsInfo = resetColumnSettings;
            } else {
                this.columnSettingsInfo.availableColumns = result.availableColumns.sort((a, b) => a.displayName.localeCompare(b.displayName));
                this.columnSettingsInfo.selectedColumns = result.selectedColumns;
            }
            this.changeDectRef.markForCheck();
        }
    }

    public resetColumnToDefault() {
        this.columnSettingsInfo = this.reportColumnService.getColumnSettingsInfo(this.dynamicColumnData);
    }

    public onDateRangeChange($event: { key: string, value: DateRangeFilterValue }) {
        if (!$event) {
            this.selectedDateRange = null;
            this.selectedReportDateRange = null;
            return;
        }
        let beginDateValue = this.reportColumnService.getDateRangeValue($event.value, 'begin');
        let endDateValue = this.reportColumnService.getDateRangeValue($event.value, 'end');
        this.selectedDateRange = {
            dateField: $event.key,
        };
        if (!beginDateValue && !endDateValue) { return; }
        if ($event.value.rangeType === DateRangeTypes.dateRange) {
            this.selectedDateRange.fromDate = beginDateValue;
            this.selectedDateRange.toDate = (moment(endDateValue).format('MM-DD-YYYY') === moment().local().format('MM-DD-YYYY') ? null : endDateValue);
        } else {
            this.selectedDateRange.daysBack = +beginDateValue;
        }
    }

    public onMyFilterChange(filter: SavedFilter) {
        this.showSaveOptions = false;
        this.filterErrorMsg = null;
        const clonedItems = clone(filter.paramsFilterContent) as any;
        Object.keys(clonedItems).
            forEach(key => {
                if (clonedItems[key as keyof FilterItems] && key !== 'dateRange') {
                    const parsedKey = JSON.parse(clonedItems[key as keyof FilterItems]);
                    clonedItems[key as keyof FilterItems] = parsedKey.map((s: any) => s.id || s);
                }
            });
        if (this.columnSettingsInfo && this.selectedFilter && this.selectedFilter.reportType === filter.reportType) {
            this.columnSettingsInfo = this.reportColumnService.updateColumnSettingsInfo(this.dynamicColumnData, filter, this.columnSettingsInfo);
        } else {
            this.getDynamicReportColumns(filter.reportType, filter);
        }
        this.selectedItemList = { ...filter, filters: clonedItems as unknown as FilterItems, isGeneratedNotSavedReport: this.isGeneratedNotSavedReport };
        this.selectedItemList.dateRange = clonedItems.dateRange;
        this.selectedFilter = { ...filter, paramsFilterContent: clonedItems };
        this.changeDectRef.markForCheck();
        this.showScheduleOptions = !!(filter.reoccurencePattern && filter.scheduleFilterContent && Object.keys(filter.scheduleFilterContent).length);
        this.showScheduleOptions ? this.disableAllActionButtons() : this.enableAllActionButtons();
    }

    public onFilterOptionsChange(selectedItemList: SelectedItemList) {
        this.filterErrorMsg = null;
        this.showSaveOptions = false;
        this.showScheduleOptions = false;
        this.enableAllActionButtons();
        this.showScheduleOptions = false;
        if (selectedItemList.reportType !== this.selectedItemList.reportType) {
            this.getDynamicReportColumns(selectedItemList.reportType, this.selectedFilter);
            this.dateRangeOptions = this.reportColumnService.getDateRangeOptions(selectedItemList.reportType);
        }
        this.selectedItemList.filters = selectedItemList.filters;
        this.selectedItemList.reportType = selectedItemList.reportType;
        this.changeDectRef.markForCheck();
    }

    public onGenerateReportClick() {
        const reportData = this.constructFilterParams();
        if (reportData) {
            const params = (this.selectedItemList && this.selectedItemList.params) ? JSON.stringify(this.selectedItemList.params) : null;
            if (params === JSON.stringify(reportData.params)) {
                this.onSaveReportClick('no');
                return;
            }
            this.showSaveOptions = true;
            this.reportMsg = null;
            this.disableAllActionButtons();
        }
    }

    public onSaveReportClick(action: string) {
        const reportData = this.constructFilterParams();
        this.showSaveOptions = false;
        if (reportData) {
            this.showActionsLoading = true;
            this.reportMsg = null;
            let reportMsg = '';
            reportData.reportRunTime = `${new DatePipe('en-US').transform(moment().local(), 'dd-MMM-yy hh:mm:ss a')} ${ Intl.DateTimeFormat().resolvedOptions().timeZone }`;

            if (action === 'save') {
                this.disableAllActionButtons();
                reportData.reportRunTime = null;
                reportData.reportAction = ReportAction.NotGeneratedSaved;
                reportMsg = 'Success! Your report has been Saved!';
            }

            if (action === 'yes') {
                this.activityLogger.logActivity('Generated Saved Report - Sample Tracking');
                this.disableAllActionButtons();
                reportData.reportAction = ReportAction.GeneratedSaved;
                reportMsg = 'Success! Your report has been Queued and Saved!';
            }

            if (action === 'no') {
                this.clearSelectedFilters = true;
                this.changeDectRef.markForCheck();
                this.activityLogger.logActivity('Generate New Report - Sample Tracking');
                this.disableAllActionButtons();
                reportMsg = 'Success! Your report has been Queued!';
            }
            this.reportService.isGenerateReportValid(reportData).subscribe((res) => {
                if (res && res.isError) {
                    this.setGenerateReportData(reportData);
                    this.showSaveOptions = false;
                    this.filterErrorMsg = res.errorMsg;
                    this.changeDectRef.markForCheck();
                    return;
                }
                this.reportService.generateReports(reportData).subscribe((generateRes) => {
                    this.setGenerateReportData(reportData);
                    this.reportMsg = reportMsg;
                    this.getSavedReports();
                });
            });

        }
    }

    public onScheduleReportClick() {
        if (this.constructFilterParams(true)) {
            this.showScheduleOptions = true;
            this.disableAllActionButtons();
        }
    }

    public onScheduleClick(event: any) { 
        if (event && event.action && event.scheduleData && event.scheduleData.scheduleParams) {
            const reportData = this.constructFilterParams(true);

            if (reportData) {
                this.showScheduleOptions = false;
                this.showActionsLoading = true;
                this.enableAllActionButtons();
                this.reportService.scheduleReports({ ...reportData, ...event.scheduleData })
                    .subscribe(() => {
                        this.reportMsg = 'Report Successfully scheduled.';
                        this.showActionsLoading = false;
                        this.getSavedReports();
                    });
            }
        } else {
            this.showScheduleOptions = false;
            this.enableAllActionButtons();
        }
    }

    public onMyFilterSubmit(filter: { action: string, userFilter: SavedFilter }) {
        this.filterErrorMsg = null;
        if (filter.action === 'new' || filter.action === 'edit') {
            this.selectedFilter = filter.userFilter;
        }

        if (filter.action === 'delete') {
            this.activityLogger.logActivity('Report Filter Deleted - Sample Tracking');
            this.showMyFilterLoading = true;
            this.changeDectRef.markForCheck();
            if (filter.userFilter && filter.userFilter.id) {
                this.reportService.deleteReport(filter.userFilter.id)
                    .subscribe(() => {
                        this.showMyFilterLoading = false;
                        this.getSavedReports();
                    });
            }
        }
    }

    private getReportDetailsById(reportId: number) {
        this.showMyFilterLoading = true;
        this.reportService.getReportById(reportId)
            .subscribe(res => {
                this.showSaveOptions = false;
                this.showMyFilterLoading = false;
                if (res.generateReportData) {
                    this.preSelectedFilterItem = res.generateReportData[0];
                }
                if (!this.dateRangeOptions && res.generateReportData[0] && res.generateReportData[0].reportType) {
                    this.dateRangeOptions = this.reportColumnService.getDateRangeOptions(res.generateReportData[0].reportType);
                }
                this.changeDectRef.markForCheck();
            });
    }

    private getSavedReports(reportId?: number) {
        this.showMyFilterLoading = true;
        this.reportService.getReports()
            .subscribe((res) => {
                this.showSaveOptions = false;
                this.showMyFilterLoading = false;
                if (res.generateReportData) {
                    if (reportId) {
                        const matchingReport = res.generateReportData.find((x: any) => x.id === reportId);
                        if (matchingReport) {
                            this.preSelectedFilterItem = matchingReport;
                            if (!this.dateRangeOptions) {
                                this.dateRangeOptions = this.reportColumnService.getDateRangeOptions(matchingReport.reportType);
                            }
                        } else {
                            this.getReportDetailsById(reportId);
                        }
                    }
                    this.myFilterItems = res.generateReportData;
                    this.myFilterItems.sort((a, b) => a.name.localeCompare(b.name));
                }
                setTimeout(
                    () => {
                        this.reportMsg = null;
                        this.changeDectRef.markForCheck();
                    },
                    5000);
                this.clearSelectedFilters = false;
                this.changeDectRef.markForCheck();
            });
    }

    private getDynamicReportColumns(reportType: number, filter?: SavedFilter) {
        if (!reportType || reportType === 0) { return; }
        this.expandEditView = false;
        let viewName = reportType === ReportType.SpecimenManagementInventoryReport ? DynamicReportView.SMInventoryReport : DynamicReportView.SampleTrackingReport;
        this.reportService.getDynamicListViewColumnInfo(viewName).subscribe((result: DynamicListViewColumnData) => {
            this.dynamicColumnData = result;
            this.columnSettingsInfo = this.reportColumnService.getColumnSettingsInfo(result);
            if (filter && this.columnSettingsInfo && filter.reportType === reportType) {
                this.columnSettingsInfo = this.reportColumnService.updateColumnSettingsInfo(this.dynamicColumnData, filter, this.columnSettingsInfo);
            }
            this.expandEditView = true;
            this.changeDectRef.markForCheck();
        });
    }

    private constructFilterParams(isScheduleReport?: boolean) {
        this.showDataLoadingError = false;
        this.filterErrorMsg = null;
        this.isReportNameError = false;
        let hasFilterSelected = false;

        if (!this.selectedFilter) {
            this.isReportNameError = true;
            this.filterErrorMsg = 'Please select an existing report or type a new report name.';
            return null;
        }

        if (this.selectedItemList && !this.selectedItemList.reportType) {
            this.filterErrorMsg = 'Please select report type.';
            return null;
        }

        if (this.selectedItemList && !this.selectedItemList.filters) {
            this.filterErrorMsg = 'Please select filters.';
            return null;
        }

        if (this.selectedItemList && ((this.selectedItemList.studyGroupName && !this.selectedItemList.studyGroupName.length) &&
            (this.selectedItemList.protocolCode && !this.selectedItemList.protocolCode.length))) {
            this.filterErrorMsg = 'Please select Study Group or Protocol.';
            return null;
        }

        if (this.selectedDateRange && this.selectedDateRange.dateField &&
            !this.selectedDateRange.daysBack && !this.selectedDateRange.fromDate) {
            this.filterErrorMsg = 'Please select Date Range';
            return null;
        }

        if (this.selectedItemList && this.selectedItemList.filters) {
            const clonedItems = clone(this.selectedItemList.filters);
            let filterParams: FilterItems = {} as FilterItems;


            Object.keys(clonedItems).
                forEach(key => {
                    if (clonedItems[key as keyof FilterItems] && clonedItems[key as keyof FilterItems].length) {
                        hasFilterSelected = true;
                        filterParams[key as keyof FilterItems] =
                            JSON.stringify(
                                clonedItems[key as keyof FilterItems].
                                    map((item: any) => {
                                        return (
                                            item.id || item
                                        );
                                    })) as any;
                    } else {
                        delete clonedItems[key as keyof FilterItems];
                    }
                });

            if (!hasFilterSelected) {
                this.filterErrorMsg = 'Please select filters.';
                return null;
            }

            if (!this.selectedItemList.studyGroupName && !this.selectedItemList.protocolCode) {
                this.showDataLoadingError = true;
                return null;
            }

            let reportInfo = {
                id: this.selectedFilter.id,
                name: this.selectedFilter.name,
                studyGroupName: (this.selectedItemList.studyGroupName.length) ? this.selectedItemList.studyGroupName : null,
                protocolCode: (this.selectedItemList.protocolCode.length) ? this.selectedItemList.protocolCode : null,
                reportType: this.selectedItemList.reportType,
                params: filterParams,
                filterJSON: JSON.parse(JSON.stringify(filterParams)),
                reportAction: ReportAction.GeneratedNotSaved,
                isGeneratedNotSavedReport: this.selectedItemList.isGeneratedNotSavedReport,
            } as Reports;
            if (this.selectedDateRange) {
                if (this.selectedDateRange.daysBack) {
                    reportInfo.dateRange = this.selectedDateRange;
                    reportInfo.filterJSON.dateRange = this.selectedDateRange;
                } else {
                    this.selectedReportDateRange = {
                        dateField: this.selectedDateRange.dateField,
                        fromDate: this.selectedDateRange.fromDate,
                        toDate: !isScheduleReport ? this.selectedDateRange.toDate : null,
                    };
                    reportInfo.dateRange = this.selectedReportDateRange;
                    reportInfo.filterJSON.dateRange = this.selectedReportDateRange;
                }
            }
            if (this.columnSettingsInfo) {
                reportInfo.configJSON = this.reportColumnService.getConfigJsonForColumnInfo(this.columnSettingsInfo, this.dynamicColumnData);
                reportInfo.appDataDefinitionId = this.dynamicColumnData.appDataDefinitionId;
            }
            reportInfo.filterJSON = JSON.stringify(reportInfo.filterJSON);
            
            return reportInfo;
        }

        return null;
    }

    private disableAllActionButtons() {
        this.disableGenerate = true;
        this.disableSave = true;
        this.disableSchedule = true;
    }

    private enableAllActionButtons() {
        this.disableGenerate = false;
        this.disableSave = false;
        this.disableSchedule = false;
    }

    private setGenerateReportData(reportData: any) {
        this.selectedItemList.params = reportData.params;
        this.showActionsLoading = false;
        this.enableAllActionButtons();
    }
}